import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom-v5-compat';

import { OpPage } from 'new-components/OpPage/OpPage';
import { OpText } from 'new-components/DLS/OpText/OpText';
import { OpSpace } from 'new-components/DLS/OpSpace/OpSpace';
import { OpTextButton } from 'new-components/DLS/OpTextButton/OpTextButton';
import avigilonAltaLogo from 'assets/img/avigilon-alta-logo-light.svg';
import { LazyRequestPermissionButton } from 'new-components/RequestPermissionButton/LazyRequestPermissionButton';
import { getOpParams } from 'utils/getOpParams';
import { useLogout } from 'utils/customHooks/useLogout';

import './NotFoundPage.scss';

export interface NotFoundPageProps {
  routeDescription?: string;
  missingScopeDescriptions?: string[];
}

/**
 * @component Page not Found component will redirect to the 404
 * route if using this component on that route
 * @returns 404 page
 */
const NotFoundPage = ({
  routeDescription,
  missingScopeDescriptions = [],
}: NotFoundPageProps = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useLogout();
  const { orgId } = getOpParams();

  const error = t('404');
  const heading = t('You seem to have veered off course');
  const subheading = t(
    "The page you are looking for doesn't\nexist or you do not have access.",
  );

  const showRequestPermission =
    routeDescription &&
    missingScopeDescriptions &&
    missingScopeDescriptions.length > 0;

  const handleGoHome = () => {
    if (orgId) {
      navigate(`/o/${orgId}/`);
    } else {
      navigate('/');
    }
  };

  return (
    <OpPage className="op-page-not-found" testId="op-page-not-found">
      <OpText size="large" fontWeight="700" className="op-page-not-found__text">
        {error}
      </OpText>
      <OpText fontWeight="500" className="op-page-not-found__heading">
        {heading}
      </OpText>
      <OpText className="op-page-not-found__subheading">{subheading}</OpText>
      <OpSpace orientation="vertical" className="op-page-not-found__actions">
        {showRequestPermission && (
          <LazyRequestPermissionButton
            routeDescription={routeDescription}
            missingScopeDescriptions={missingScopeDescriptions}
          />
        )}
        <OpSpace orientation="horizontal" size="middle">
          <OpTextButton onClick={handleGoHome}>{t('Go home')}</OpTextButton>
          <OpTextButton onClick={logout}>{t('Logout')}</OpTextButton>
        </OpSpace>
      </OpSpace>
      <img
        className="op-page-not-found__alta-logo"
        src={avigilonAltaLogo}
        alt="Logo"
      />
    </OpPage>
  );
};

export default NotFoundPage;
